var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { faExclamation, faSquare, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BBadge, BButton, BTab, BTabs } from "bootstrap-vue";
import { computed, onUnmounted, ref, watch } from "vue";
import {} from "@/api/invocations";
import { useAnimationFrameResizeObserver } from "@/composables/sensors/animationFrameResizeObserver";
import { useInvocationStore } from "@/stores/invocationStore";
import { useWorkflowStore } from "@/stores/workflowStore";
import { errorMessageAsString } from "@/utils/simple-error";
import { cancelWorkflowScheduling } from "./services";
import { errorCount as jobStatesSummaryErrorCount, isTerminal, jobCount as jobStatesSummaryJobCount, numTerminal, okCount as jobStatesSummaryOkCount, runningCount as jobStatesSummaryRunningCount, } from "./util";
import ProgressBar from "../ProgressBar.vue";
import WorkflowInvocationSteps from "../Workflow/Invocation/Graph/WorkflowInvocationSteps.vue";
import InvocationReport from "../Workflow/InvocationReport.vue";
import WorkflowAnnotation from "../Workflow/WorkflowAnnotation.vue";
import WorkflowNavigationTitle from "../Workflow/WorkflowNavigationTitle.vue";
import WorkflowInvocationExportOptions from "./WorkflowInvocationExportOptions.vue";
import WorkflowInvocationInputOutputTabs from "./WorkflowInvocationInputOutputTabs.vue";
import WorkflowInvocationMetrics from "./WorkflowInvocationMetrics.vue";
import WorkflowInvocationOverview from "./WorkflowInvocationOverview.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'WorkflowInvocationState',
    props: {
        invocationId: null,
        isSubworkflow: { type: Boolean, default: false },
        isFullPage: { type: Boolean },
        success: { type: Boolean },
        newHistoryTarget: { type: Boolean }
    },
    emits: ["invocation-cancelled"],
    setup(__props, { emit }) {
        const props = __props;
        const invocationStore = useInvocationStore();
        const stepStatesInterval = ref(undefined);
        const jobStatesInterval = ref(undefined);
        const invocationLoaded = ref(false);
        const errorMessage = ref(null);
        // after the report tab is first activated, no longer lazy-render it from then on
        const reportActive = ref(false);
        const reportLazy = ref(true);
        watch(() => reportActive.value, (newValue) => {
            if (newValue) {
                reportLazy.value = false;
            }
        });
        const workflowStore = useWorkflowStore();
        const reportTabDisabled = computed(() => !invocationStateSuccess.value ||
            !invocation.value ||
            !workflowStore.getStoredWorkflowByInstanceId(invocation.value.workflow_id));
        /** Tooltip message for the report tab when it is disabled */
        const disabledReportTooltip = computed(() => {
            const state = invocationState.value;
            if (state != "scheduled") {
                return `This workflow is not currently scheduled. The current state is ${state}. Once the workflow is fully scheduled and jobs have complete this option will become available.`;
            }
            else if (runningCount.value != 0) {
                return `The workflow invocation still contains ${runningCount.value} running job(s). Once these jobs have completed this option will become available.`;
            }
            else {
                return "Steps for this workflow are still running. A report will be available once complete.";
            }
        });
        const invocationTabs = ref();
        const scrollableDiv = computed(() => { var _a; return (_a = invocationTabs.value) === null || _a === void 0 ? void 0 : _a.$el.querySelector(".tab-content"); });
        const isScrollable = ref(false);
        useAnimationFrameResizeObserver(scrollableDiv, ({ clientSize, scrollSize }) => {
            isScrollable.value = scrollSize.height >= clientSize.height + 1;
        });
        const invocation = computed(() => invocationLoaded.value
            ? invocationStore.getInvocationById(props.invocationId)
            : null);
        const invocationState = computed(() => { var _a; return ((_a = invocation.value) === null || _a === void 0 ? void 0 : _a.state) || "new"; });
        const invocationAndJobTerminal = computed(() => invocationSchedulingTerminal.value && jobStatesTerminal.value);
        const invocationSchedulingTerminal = computed(() => {
            return (invocationState.value == "scheduled" ||
                invocationState.value == "cancelled" ||
                invocationState.value == "failed");
        });
        const jobStatesTerminal = computed(() => {
            if (invocationSchedulingTerminal.value && jobCount.value === 0) {
                // no jobs for this invocation (think subworkflow or just inputs)
                return true;
            }
            return !!jobStatesSummary.value && isTerminal(jobStatesSummary.value);
        });
        const jobStatesSummary = computed(() => {
            const jobsSummary = invocationStore.getInvocationJobsSummaryById(props.invocationId);
            return (!jobsSummary ? null : jobsSummary);
        });
        const invocationStateSuccess = computed(() => {
            return invocationState.value == "scheduled" && runningCount.value === 0 && invocationAndJobTerminal.value;
        });
        const stepStates = computed(() => {
            var _a;
            const stepStates = {};
            const steps = ((_a = invocation.value) === null || _a === void 0 ? void 0 : _a.steps) || [];
            for (const step of steps) {
                if (!step) {
                    continue;
                }
                // the API defined state here allowing null and undefined is odd...
                const stepState = step.state || "unknown";
                if (!stepStates[stepState]) {
                    stepStates[stepState] = 1;
                }
                else {
                    stepStates[stepState] += 1;
                }
            }
            return stepStates;
        });
        const stepCount = computed(() => {
            var _a;
            return ((_a = invocation.value) === null || _a === void 0 ? void 0 : _a.steps.length) || 0;
        });
        const stepStatesStr = computed(() => {
            var _a;
            return `${((_a = stepStates.value) === null || _a === void 0 ? void 0 : _a.scheduled) || 0} of ${stepCount.value} steps successfully scheduled.`;
        });
        const okCount = computed(() => {
            return jobStatesSummaryOkCount(jobStatesSummary.value);
        });
        const errorCount = computed(() => {
            return jobStatesSummaryErrorCount(jobStatesSummary.value);
        });
        const runningCount = computed(() => {
            return jobStatesSummaryRunningCount(jobStatesSummary.value);
        });
        const jobCount = computed(() => {
            return jobStatesSummaryJobCount(jobStatesSummary.value);
        });
        const newCount = computed(() => {
            return jobCount.value - okCount.value - runningCount.value - errorCount.value;
        });
        const jobStatesStr = computed(() => {
            let jobStr = `${numTerminal(jobStatesSummary.value) || 0} of ${jobCount.value} jobs complete`;
            if (!invocationSchedulingTerminal.value) {
                jobStr += " (total number of jobs will change until all steps fully scheduled)";
            }
            return `${jobStr}.`;
        });
        watch(() => props.invocationId, (id) => __awaiter(this, void 0, void 0, function* () {
            invocationLoaded.value = false;
            try {
                yield invocationStore.fetchInvocationForId({ id });
                invocationLoaded.value = true;
                // Only start polling if there is a valid invocation
                if (invocation.value) {
                    yield pollStepStatesUntilTerminal();
                    yield pollJobStatesUntilTerminal();
                }
            }
            catch (e) {
                errorMessage.value = errorMessageAsString(e);
            }
        }), { immediate: true });
        const storeId = computed(() => (invocation.value ? `invocation-${invocation.value.id}` : undefined));
        onUnmounted(() => {
            clearTimeout(stepStatesInterval.value);
            clearTimeout(jobStatesInterval.value);
        });
        function pollStepStatesUntilTerminal() {
            return __awaiter(this, void 0, void 0, function* () {
                if (!invocationSchedulingTerminal.value) {
                    yield invocationStore.fetchInvocationForId({ id: props.invocationId });
                    stepStatesInterval.value = setTimeout(pollStepStatesUntilTerminal, 3000);
                }
            });
        }
        function pollJobStatesUntilTerminal() {
            return __awaiter(this, void 0, void 0, function* () {
                if (!jobStatesTerminal.value) {
                    yield invocationStore.fetchInvocationJobsSummaryForId({ id: props.invocationId });
                    jobStatesInterval.value = setTimeout(pollJobStatesUntilTerminal, 3000);
                }
            });
        }
        function onError(e) {
            console.error(e);
        }
        function onCancel() {
            emit("invocation-cancelled");
        }
        function cancelWorkflowSchedulingLocal() {
            cancelWorkflowScheduling(props.invocationId).then(onCancel).catch(onError);
        }
        return { __sfc: true, props, emit, invocationStore, stepStatesInterval, jobStatesInterval, invocationLoaded, errorMessage, reportActive, reportLazy, workflowStore, reportTabDisabled, disabledReportTooltip, invocationTabs, scrollableDiv, isScrollable, invocation, invocationState, invocationAndJobTerminal, invocationSchedulingTerminal, jobStatesTerminal, jobStatesSummary, invocationStateSuccess, stepStates, stepCount, stepStatesStr, okCount, errorCount, runningCount, jobCount, newCount, jobStatesStr, storeId, pollStepStatesUntilTerminal, pollJobStatesUntilTerminal, onError, onCancel, cancelWorkflowSchedulingLocal, faExclamation, faSquare, faTimes, FontAwesomeIcon, BAlert, BBadge, BButton, BTab, BTabs, ProgressBar, WorkflowInvocationSteps, InvocationReport, WorkflowAnnotation, WorkflowNavigationTitle, WorkflowInvocationExportOptions, WorkflowInvocationInputOutputTabs, WorkflowInvocationMetrics, WorkflowInvocationOverview, LoadingSpan };
    }
});
